import {
  HOME_TIER_MONTHLY_PRICE,
  HOME_TIER_ONE_TIME_PRICE,
  HOME_TIER_PRIMARY_PRICING_COPY,
  HOME_TIER_SECONDARY_PRICING_COPY,
} from "@panelgenie/contracts";
import { Badge, Card, Heading } from "@panelgenie/shared-ui";

/**
 * Anonymous-accessible pricing page per `MVP.md` §6 item 7
 * ("Pricing page in the app reflects Marketing §4").
 *
 * Mounted at `/pricing`. The MVP §4.9 verbatim wording ("Unlock
 * everything for $24.99 (one-time). Year 1 AI included." + "Or
 * $2.99/month") leads the page; the two standalone price badges
 * give the scanning-eye a quick read.
 *
 * Pure-presentation: no Clerk hooks, no tRPC, no seam wiring.
 */
export const PricingPage = () => {
  return (
    <main
      aria-label="pricing-page"
      className="mx-auto max-w-2xl px-4 py-10 sm:py-14 text-fg"
    >
      <header className="text-center">
        <Heading level="h1" className="mb-2">
          Panel Genie
        </Heading>
        <p className="text-base text-mute">
          AI-assisted electrical-panel labeling and documentation.
        </p>
      </header>

      <Card
        variant="elevated"
        aria-label="pricing-page-home-tier"
        className="mt-8"
      >
        <Card.Header>
          <Heading level="h2">Genie Home</Heading>
        </Card.Header>
        <Card.Body>
          <p
            aria-label="pricing-page-primary"
            className="text-base font-medium text-fg"
          >
            {HOME_TIER_PRIMARY_PRICING_COPY}
          </p>
          <p
            aria-label="pricing-page-secondary"
            className="mt-1 text-base text-mute"
          >
            {HOME_TIER_SECONDARY_PRICING_COPY}
          </p>

          <div
            aria-label="pricing-page-price-chips"
            className="mt-4 flex flex-wrap gap-3"
          >
            <Badge
              variant="accent"
              aria-label="pricing-page-one-time-chip"
              className="text-sm px-3 py-1"
            >
              <strong className="text-base">{HOME_TIER_ONE_TIME_PRICE}</strong>{" "}
              one-time
            </Badge>
            <Badge
              variant="neutral"
              aria-label="pricing-page-monthly-chip"
              className="text-sm px-3 py-1"
            >
              <strong className="text-base">{HOME_TIER_MONTHLY_PRICE}</strong>
              /month
            </Badge>
          </div>
        </Card.Body>
      </Card>

      <Card
        variant="inset"
        aria-label="pricing-page-lite-tier"
        className="mt-4"
      >
        <Card.Header>
          <Heading level="h3">Genie Lite</Heading>
        </Card.Header>
        <Card.Body>
          <p className="text-base text-mute">
            Free. One site, one panel, twenty circuits, eight lifetime AI
            scans, watermarked outputs. Upgrade to Home when you outgrow
            the caps.
          </p>
        </Card.Body>
      </Card>

      <footer className="mt-6 text-center text-xs text-dim">
        Prices in USD. Year 1 AI credits are included with the one-time
        Home unlock.
      </footer>
    </main>
  );
};
